
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTheme } from '@/components/theme/theme-provider';
import { useLanguage } from '@/components/i18n/language-provider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NavBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { theme } = useTheme();
  const { language, setLanguage, t } = useLanguage();

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.products'), href: '/products' },
    { name: 'Solutions', href: '/solutions' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Control body scrolling when mobile menu changes
  useEffect(() => {
    if (mobileMenuOpen) {
      // Disable scrolling when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling when menu is closed
      document.body.style.overflow = '';
    }
    
    // Cleanup function to ensure scroll is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-4 px-6 md:px-8",
        isScrolled 
          ? "md:glass-effect md:shadow-sm bg-white shadow-sm dark:bg-background" 
          : "bg-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo on the left */}
        <div className="flex-shrink-0">
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/1ced5bdd-e5b1-4766-bd40-4c543af82736.png" 
              alt="Keystle Logo" 
              className="h-10 w-10" 
            />
            <span className="ml-2 text-xl font-display font-bold">keystle</span>
          </Link>
        </div>

        {/* Centered navigation */}
        <nav className="hidden md:flex items-center justify-center flex-1 mx-4">
          <div className="flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "font-medium transition-colors duration-200 hover:text-brand-pink",
                  location.pathname === item.href ? "text-brand-pink" : "text-foreground/80"
                )}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </nav>

        {/* Right side controls */}
        <div className="hidden md:flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                aria-label="Change language"
              >
                <Globe className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLanguage("en-US")}>
                <span className={cn(
                  "flex items-center",
                  language === "en-US" && "font-bold text-brand-pink"
                )}>
                  🇺🇸 {t('lang.en')}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage("pt-BR")}>
                <span className={cn(
                  "flex items-center",
                  language === "pt-BR" && "font-bold text-brand-pink"
                )}>
                  🇧🇷 {t('lang.pt')}
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button 
            className="bg-[#843868] hover:bg-[#843868]/90 text-white"
            onClick={() => window.open("https://meetings-na2.hubspot.com/gilmar/demo-keystle-website", "_blank")}
          >
            {t('nav.contactUs')}
          </Button>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="md:hidden -m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-gray-200"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span className="sr-only">Toggle menu</span>
          {mobileMenuOpen ? (
            <X className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Menu className="h-6 w-6" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={cn(
          "md:hidden fixed inset-x-0 top-16 bottom-0 z-40 bg-white dark:bg-background transition-transform duration-300 ease-in-out",
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="space-y-1 p-6 pt-8 flex flex-col">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "py-3 text-lg font-medium transition-colors duration-200 hover:text-brand-pink",
                location.pathname === item.href ? "text-brand-pink" : "text-foreground/80"
              )}
            >
              {item.name}
            </Link>
          ))}
          <div className="flex flex-col pt-6 gap-3">
            <Button
              variant="outline"
              className="w-full justify-between px-4"
              onClick={() => setLanguage(language === "en-US" ? "pt-BR" : "en-US")}
            >
              {language === "en-US" ? "🇺🇸 English" : "🇧🇷 Português"}
              <Globe className="h-4 w-4" />
            </Button>
            <Button 
              className="w-full justify-center bg-[#843868] hover:bg-[#843868]/90 text-white"
              onClick={() => window.open("https://meetings-na2.hubspot.com/gilmar/demo-keystle-website", "_blank")}
            >
              {t('nav.contactUs')}
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavBar;
